import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { FC } from 'react'

import { useGameEventContext } from 'features/game/event-context'
import { useGameContext } from 'features/game/game-context'
import { getCenterOfHexes } from 'features/game/utils'
import { HexId } from 'features/hex-board/hex-board'
import { getNeighbors, isNeighbor } from 'features/hex-board/utils'

import { getEntries, getKeys } from 'shared/utils'

import { allowedInitialHexes, isAlly, isEmptyHex, resolveConflicts } from '../utils'
import { Hex } from './hex'

export const HexBoard: FC = () => {
  const {
    phase,
    hexBoard,
    boardState: { switchRestrictions },
    relativeGameData: { playerColor },
  } = useGameContext()
  const { hexSelected, handPieceSelectedIdx } = useGameEventContext()

  const possibleMoves = new Set<HexId>()
  if (phase === 'distribute') {
    if (handPieceSelectedIdx !== null) {
      allowedInitialHexes[playerColor.bottom].forEach(hexId => {
        if (isEmptyHex(hexBoard[hexId])) {
          possibleMoves.add(hexId)
        }
      })
    }
  } else if (phase === 'fight') {
    getEntries(hexBoard).forEach(([hexId, piece]) => {
      const neighbors = getNeighbors(hexId)

      if (hexSelected && isNeighbor(hexSelected, hexId) && (isAlly(piece, playerColor.bottom) || isEmptyHex(piece))) {
        possibleMoves.add(hexId)
      }

      if (
        handPieceSelectedIdx !== null &&
        neighbors.filter(nb => isAlly(hexBoard[nb], playerColor.bottom)).length > 0
      ) {
        possibleMoves.add(hexId)
      }
    })
  }

  const xElements = switchRestrictions.map(res => {
    const { x, y } = getCenterOfHexes(playerColor.bottom, res)
    return <X key={`${x}-${y}`} x={x} y={y} size={120} stroke="red" strokeWidth={3} />
  })

  const potentialBoardAfterFight = resolveConflicts(hexBoard)

  if (!hexBoard) return null

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5, duration: 0.5, type: 'spring' }}
      className="cursor-pointer"
    >
      {getKeys(hexBoard).map((hexId, idx) => (
        <Hex
          key={idx}
          hexId={hexId}
          piece={hexBoard[hexId]}
          potentialMove={possibleMoves.has(hexId)}
          potentiallyDies={potentialBoardAfterFight[hexId] === null}
        />
      ))}

      {xElements}
    </motion.g>
  )
}
